import { useEffect, useRef, useState, RefObject } from "react";

interface UseInViewOptions {
  /** 触发的阈值，0-1之间，0表示一点点进入视图就触发，1表示完全进入视图才触发 */
  threshold?: number | number[];
  /** 根元素的外边距，用于扩展或缩小根的边界框 */
  rootMargin?: string;
  /** 指定根元素，默认为浏览器视口 */
  root?: Element | null;
  /** 是否只触发一次，默认false */
  triggerOnce?: boolean;
  /** 是否跳过初始的可见性检查，默认false */
  skip?: boolean;
}

interface UseInViewReturn {
  /** 元素的引用 */
  ref: React.RefObject<HTMLDivElement>;
  /** 是否在视图内 */
  inView: boolean;
  /** IntersectionObserver的entry对象 */
  entry?: IntersectionObserverEntry;
}

/**
 * 自定义Hook，用于检测元素是否在视口内
 * @param options 配置选项
 * @returns 包含ref、inView状态和entry的对象
 */
export const useInView = (options: UseInViewOptions = {}): UseInViewReturn => {
  const {
    threshold = 0,
    root = null,
    rootMargin = "0px",
    triggerOnce = false,
    skip = false,
  } = options;

  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [entry, setEntry] = useState<IntersectionObserverEntry>();

  useEffect(() => {
    if (skip || !ref.current) return;

    // 检查浏览器是否支持 IntersectionObserver
    if (!window.IntersectionObserver) {
      console.warn("IntersectionObserver is not supported");
      setInView(true);
      return;
    }

    const element = ref.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isInView = entry.isIntersecting;

        setInView(isInView);
        setEntry(entry);

        // 如果设置了只触发一次，且当前在视图内，则停止观察
        if (triggerOnce && isInView) {
          observer.unobserve(element);
        }
      },
      {
        threshold,
        root,
        rootMargin,
      },
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, root, rootMargin, triggerOnce, skip]);

  return { ref, inView, entry };
};

export default useInView;
